import React, { useState, useEffect } from "react";
import {
  Box,
  Badge,
  Button,
  Flex,
  Grid,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { getLongGradient, getShortGradient } from "../../../../utils/signalColors";

export interface Signal {
  id: string;
  /** Backend event identifier used for execute endpoint (POST /signals/{event_id}/execute) */
  eventId?: string;
  type: "LONG" | "SHORT";
  entry: string;
  exit?: string;
  tp: string;
  sl: string;
  rr: string;
  pp?: string; // Potential Profit (dollar amount only for card)
  ppWithROI?: string; // Potential Profit with ROI percentage (for modal)
  expiryTime: Date; // When the signal expires
  createdAt: Date; // When the signal was created
  reasoning?: string; // Reasoning behind the signal
  symbol?: string; // Instrument e.g. "XAU/USD" for grouping/storage
  /** Initial numeric RR from backend (optional); modal can refine using calculate-risk. */
  initialRiskRewardRatio?: number | null;
  /** Optional gate/strategy used by the arbiter (from regime_context.gate_used) */
  gateUsed?: string | null;
  /** Optional dynamic RR chosen by the gate (from regime_context.gate_rr) */
  gateRr?: number | null;
}

interface SignalCardProps {
  signal: Signal;
  /** Number of open trades taken from this setup (for "Taken × N" and card shade) */
  takenCount?: number;
  onPlaceOrder?: (signal: Signal) => void;
  onClick?: (signal: Signal) => void;
}

const SignalCard: React.FC<SignalCardProps> = ({ signal, takenCount = 0, onPlaceOrder, onClick }) => {
  const isLong = signal.type === "LONG";
  const [timeRemaining, setTimeRemaining] = useState<string>("");
  const [progress, setProgress] = useState<number>(0);
  const [progressColor, setProgressColor] = useState<string>("#06E363");
  const [isFlashing, setIsFlashing] = useState<boolean>(false);
  const [badgeBg, setBadgeBg] = useState<string>(isLong ? getLongGradient() : getShortGradient());
  
  // Listen for signal color changes
  useEffect(() => {
    const updateColors = () => {
      setBadgeBg(isLong ? getLongGradient() : getShortGradient());
    };
    
    updateColors();
    window.addEventListener('signalColorsChanged', updateColors);
    
    return () => {
      window.removeEventListener('signalColorsChanged', updateColors);
    };
  }, [isLong]);
  
  // Calculate and update time remaining and progress
  useEffect(() => {
    const updateTimeRemaining = () => {
      const now = new Date();
      const expiry = new Date(signal.expiryTime);
      const diff = expiry.getTime() - now.getTime();
      
      if (diff <= 0) {
        setTimeRemaining("0s");
        setProgress(100);
        setProgressColor("#E00101");
        setIsFlashing(false); // Stop flashing when expired
        return;
      }
      
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      const totalSeconds = minutes * 60 + seconds;
      
      // Check if 15 seconds or less remaining
      setIsFlashing(totalSeconds <= 15 && totalSeconds > 0);
      
      if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }
      
      // Calculate progress percentage (0% = just created, 100% = expired)
      const createdAt = new Date(signal.createdAt);
      const totalDuration = expiry.getTime() - createdAt.getTime(); // Total duration in ms
      const elapsed = now.getTime() - createdAt.getTime(); // Time elapsed in ms
      const progressPercent = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
      setProgress(progressPercent);
      
      // Calculate gradient color: green (0%) → yellow (50%) → red (100%)
      let r, g, b;
      if (progressPercent < 50) {
        // Green (#06E363 = rgb(6, 227, 99)) to Yellow (#FFFF00 = rgb(255, 255, 0))
        const ratio = progressPercent / 50;
        r = Math.round(6 + (255 - 6) * ratio);
        g = Math.round(227 + (255 - 227) * ratio);
        b = Math.round(99 + (0 - 99) * ratio);
      } else {
        // Yellow (#FFFF00 = rgb(255, 255, 0)) to Red (#E00101 = rgb(224, 1, 1))
        const ratio = (progressPercent - 50) / 50;
        r = Math.round(255 - (255 - 224) * ratio);
        g = Math.round(255 - (255 - 1) * ratio);
        b = Math.round(0 + (1 - 0) * ratio);
      }
      setProgressColor(`rgb(${r}, ${g}, ${b})`);
    };
    
    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 100);
    
    return () => clearInterval(interval);
  }, [signal.expiryTime, signal.createdAt]);
  
  const buttonTextGradient = "linear-gradient(135deg, #38BBD1, #3EEAEC)"; // Primary gradient (Tropical Surf to Neon Aqua)
  const buttonStrokeColor = "#38BBD1"; // Tropical Surf
  const cardStrokeColor = "#D3D3D3"; // Soft Glacial Grey
  const cardBg = useColorModeValue("white", "#151517");
  const textColor = useColorModeValue("#575757", "white");
  const labelColor = useColorModeValue("gray.400", "gray.400");
  const progressBgColor = useColorModeValue("#E5E7EB", "#4A5568");
  const actionText = isLong ? "Buy" : "Sell";
  const takenBadgeBg = useColorModeValue("gray.100", "gray.700");
  const takenShadeBg = useColorModeValue("rgba(56, 187, 209, 0.06)", "rgba(56, 187, 209, 0.12)");

  const formatGateName = (gate?: string | null): string | null => {
    if (!gate) return null;
    switch (gate) {
      case "pivot":
        return "Pivot";
      case "pivot_relaxed":
        return "Pivot (relaxed)";
      case "mrd":
        return "MRD";
      case "mrd_relaxed":
        return "MRD (relaxed)";
      case "discount_purchase":
        return "Discount purchase";
      case "velocity_breakout":
        return "Velocity breakout";
      case "both":
        return "Both";
      default:
        // Fallback: humanize by replacing underscores and capitalizing first letter
        return gate.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
    }
  };

  const prettyGateName = formatGateName(signal.gateUsed ?? null);
  const gateRrText =
    typeof signal.gateRr === "number"
      ? `RR ${signal.gateRr.toFixed(2)}x`
      : null;

  const isExpired = progress >= 100;
  const isTaken = takenCount >= 1;

  return (
    <Box
      borderRadius="md"
      border="1px solid"
      borderColor={cardStrokeColor}
      borderLeftWidth={isTaken ? "3px" : "1px"}
      borderLeftColor={isTaken ? "#38BBD1" : undefined}
      mb={2}
      bg={isTaken ? takenShadeBg : cardBg}
      p={{ base: 1.5, md: 2 }}
      animation={
        isFlashing 
          ? "pulse 1s ease-in-out infinite" 
          : isExpired 
          ? "slideOut 0.5s ease-out forwards" 
          : "slideIn 0.4s ease-out"
      }
      cursor="pointer"
      onClick={() => onClick?.(signal)}
      _hover={{
        transform: "translateY(-2px)",
        transition: "transform 0.2s",
      }}
      sx={{
        "@keyframes pulse": {
          "0%, 100%": {
            opacity: 1,
          },
          "50%": {
            opacity: 0.5,
          },
        },
        "@keyframes slideIn": {
          "0%": {
            opacity: 0,
            transform: "translateY(-20px)",
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0)",
          },
        },
        "@keyframes slideOut": {
          "0%": {
            opacity: 1,
            transform: "translateY(0)",
          },
          "100%": {
            opacity: 0,
            transform: "translateY(-20px)",
          },
        },
      }}
    >
      <Box
        h="100%"
        position="relative"
        bg={isTaken ? takenShadeBg : cardBg}
        w="100%"
        minW="0"
        css={{
          background: isTaken ? takenShadeBg : cardBg,
        }}
      >
        {/* @ts-ignore - Chakra UI responsive props create complex union types */}
        <Box display="flex" flexDirection="column" gap={{ base: 2, md: 3 }} w="100%" minW="0">
          {/* First Row: Badge, Entry, TP, SL (always visible) */}
          {/* @ts-ignore - Chakra UI responsive props create complex union types */}
          <Flex align="center" gap={{ base: 1.5, md: 3 }} w="100%" minW="0">
            {/* LONG/SHORT Badge on the left */}
            <Badge
              bg={badgeBg}
              color="white"
              px={{ base: 2, md: 3 }}
              py={{ base: 1, md: 1.5 }}
              borderRadius="md"
              fontSize="xs"
              fontWeight="bold"
              background={badgeBg}
              flexShrink={0}
            >
              {signal.type}
            </Badge>

            {isTaken && (
              <Badge
                bg={takenBadgeBg}
                color={textColor}
                px={2}
                py={1}
                borderRadius="md"
                fontSize="xs"
                fontWeight="medium"
                flexShrink={0}
                fontFamily="'IBM Plex Sans', sans-serif"
              >
                Taken × {takenCount}
              </Badge>
            )}

            {prettyGateName && (
              <Badge
                bg={takenBadgeBg}
                color={textColor}
                px={2}
                py={1}
                borderRadius="md"
                fontSize="xs"
                fontWeight="medium"
                flexShrink={0}
                fontFamily="'IBM Plex Sans', sans-serif"
              >
                Gate: {prettyGateName}
                {gateRrText ? ` (${gateRrText})` : ""}
              </Badge>
            )}

            {/* Entry Price */}
            <Box flex="1" minW={{ base: "45px", md: "60px" }}>
              <Text fontSize="xs" color={labelColor} mb={0.5}>
                Entry
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color={textColor} fontWeight="medium" noOfLines={1}>
                {signal.entry}
              </Text>
            </Box>

            {/* TP */}
            <Box flex="1" minW={{ base: "45px", md: "60px" }}>
              <Text fontSize="xs" color={labelColor} mb={0.5}>
                TP
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color={textColor} fontWeight="medium" noOfLines={1}>
                {signal.tp}
              </Text>
            </Box>

            {/* SL */}
            <Box flex="1" minW={{ base: "45px", md: "60px" }}>
              <Text fontSize="xs" color={labelColor} mb={0.5}>
                SL
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color={textColor} fontWeight="medium" noOfLines={1}>
                {signal.sl}
              </Text>
            </Box>

            {/* Circular Progress Indicator with Expiry Time - Hidden on mobile */}
            <Box 
              position="relative" 
              w="40px" 
              h="40px" 
              flexShrink={0}
              display={{ base: "none", md: "block" }}
            >
              <svg width="40" height="40" style={{ transform: "rotate(-90deg)" }}>
                {/* Background circle */}
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke={progressBgColor}
                  strokeWidth="2.5"
                />
                {/* Progress circle - color changes based on progress */}
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke={progressColor}
                  strokeWidth="2.5"
                  strokeDasharray={`${2 * Math.PI * 16}`}
                  strokeDashoffset={`${2 * Math.PI * 16 * (1 - progress / 100)}`}
                  strokeLinecap="round"
                  style={{ 
                    transition: "stroke-dashoffset 0.1s linear, stroke 0.1s linear"
                  }}
                />
              </svg>
              {/* Time remaining text in center */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                textAlign="center"
                w="100%"
              >
                <Text 
                  fontSize="xs" 
                  fontWeight="normal" 
                  color={textColor} 
                  lineHeight="1.2"
                  fontFamily="'IBM Plex Sans', sans-serif"
                >
                  {timeRemaining.includes("m") ? timeRemaining.split(" ")[0] : timeRemaining}
                </Text>
              </Box>
            </Box>

            {/* Take Trade Button - Hidden on mobile */}
            <Box
              as="button"
              type="button"
              onClick={(e: any) => {
                e.stopPropagation();
                onPlaceOrder?.(signal);
              }}
              borderRadius="md"
              border="1px solid"
              borderColor={buttonStrokeColor}
              p="1px"
              flexShrink={0}
              cursor="pointer"
              bg="transparent"
              display={{ base: "none", md: "block" }}
              _focus={{
                outline: "none",
                boxShadow: "none",
              }}
            >
              <Box
                borderRadius="md"
                bg="transparent"
                px={4}
                py={1.5}
                fontSize="xs"
                fontWeight="medium"
                background={buttonTextGradient}
                backgroundClip="text"
                w="100%"
                css={{
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                }}
              >
                Take Trade
              </Box>
            </Box>
          </Flex>

          {/* Second Row: Expiry Time and Take Trade Button (mobile only) */}
          <Flex 
            align="center" 
            justify="space-between" 
            gap={{ base: 2, md: 3 }} 
            w="100%" 
            minW="0"
            display={{ base: "flex", md: "none" }}
          >
            {/* Circular Progress Indicator with Expiry Time */}
            <Box position="relative" w={{ base: "32px", md: "40px" }} h={{ base: "32px", md: "40px" }} flexShrink={0}>
              <svg width="100%" height="100%" viewBox="0 0 40 40" style={{ transform: "rotate(-90deg)" }}>
                {/* Background circle */}
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke={progressBgColor}
                  strokeWidth="2.5"
                />
                {/* Progress circle - color changes based on progress */}
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke={progressColor}
                  strokeWidth="2.5"
                  strokeDasharray={`${2 * Math.PI * 16}`}
                  strokeDashoffset={`${2 * Math.PI * 16 * (1 - progress / 100)}`}
                  strokeLinecap="round"
                  style={{ 
                    transition: "stroke-dashoffset 0.1s linear, stroke 0.1s linear"
                  }}
                />
              </svg>
              {/* Time remaining text in center */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                textAlign="center"
                w="100%"
              >
                <Text 
                  fontSize="xs" 
                  fontWeight="normal" 
                  color={textColor} 
                  lineHeight="1.2"
                  fontFamily="'IBM Plex Sans', sans-serif"
                >
                  {timeRemaining.includes("m") ? timeRemaining.split(" ")[0] : timeRemaining}
                </Text>
              </Box>
            </Box>

            {/* Take Trade Button */}
            <Box
              as="button"
              type="button"
              onClick={(e: any) => {
                e.stopPropagation();
                onPlaceOrder?.(signal);
              }}
              borderRadius="md"
              border="1px solid"
              borderColor={buttonStrokeColor}
              p="1px"
              flexShrink={0}
              cursor="pointer"
              bg="transparent"
              flex="1"
              _focus={{
                outline: "none",
                boxShadow: "none",
              }}
            >
              <Box
                borderRadius="md"
                bg="transparent"
                px={{ base: 3, md: 4 }}
                py={{ base: 1, md: 1.5 }}
                fontSize="xs"
                fontWeight="medium"
                background={buttonTextGradient}
                backgroundClip="text"
                w="100%"
                css={{
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                }}
              >
                Take Trade
              </Box>
            </Box>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default SignalCard;
